import { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react';
import { toYMD, todayYMD, parseYMD } from '../utils/dates';
import { getActivityCalendarDates } from '../api/api';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const COLORS = {
  green: {
    selected: 'bg-green-600 text-white border-green-600',
    today: 'border-green-500 text-green-700',
    dot: 'bg-green-500',
  },
  blue: {
    selected: 'bg-blue-600 text-white border-blue-600',
    today: 'border-blue-500 text-blue-700',
    dot: 'bg-blue-500',
  },
};

const CHUNK = 30; // days per extension
const INITIAL_BACK = 30;
const INITIAL_FORWARD = 60;

function buildDates(startOffset, endOffset) {
  const today = parseYMD(todayYMD());
  const result = [];
  for (let i = startOffset; i <= endOffset; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    result.push(d);
  }
  return result;
}

/**
 * Horizontally scrollable day/date strip.
 * - Starts centered on today.
 * - Dynamically extends left/right when user scrolls near the edges.
 * - Fetches activity-date dots from the backend for each newly visible range.
 * Note: activityDates prop is kept for backward-compat but ignored (API-driven now).
 */
const DateStrip = ({ selectedDates = [], onSelectDate, color = 'green' }) => {
  const today = todayYMD();
  const c = COLORS[color] || COLORS.green;

  const [startOffset, setStartOffset] = useState(-INITIAL_BACK);
  const [endOffset, setEndOffset]     = useState(INITIAL_FORWARD);
  const [activityDates, setActivityDates] = useState(new Set());

  const fetchedRanges = useRef(new Set()); // 'from_to' keys already fetched
  const containerRef  = useRef(null);
  const todayRef      = useRef(null);
  const isExtending   = useRef(false);
  const prevScrollRef = useRef({ width: 0, adjustLeft: false });

  // Fetch calendar dots for a date range (cached by range key)
  const fetchDots = useCallback(async (fromOff, toOff) => {
    const base = parseYMD(today);
    const from = toYMD(new Date(base.getTime() + fromOff * 86400000));
    const to   = toYMD(new Date(base.getTime() + toOff * 86400000));
    const key  = `${from}_${to}`;
    if (fetchedRanges.current.has(key)) return;
    fetchedRanges.current.add(key);
    try {
      const res = await getActivityCalendarDates(from, to);
      const newDates = res.data?.dates || [];
      if (newDates.length > 0) {
        setActivityDates(prev => {
          const next = new Set(prev);
          newDates.forEach(d => next.add(d));
          return next;
        });
      }
    } catch {}
  }, [today]);

  // Initial fetch + center-today scroll
  useEffect(() => {
    fetchDots(-INITIAL_BACK, INITIAL_FORWARD);
  }, []); // eslint-disable-line

  useEffect(() => {
    if (todayRef.current && containerRef.current) {
      const el   = todayRef.current;
      const cont = containerRef.current;
      cont.scrollLeft = el.offsetLeft - cont.clientWidth / 2 + el.offsetWidth / 2;
    }
  }, []); // eslint-disable-line

  // Adjust scroll after left-extension (prevent jump)
  useLayoutEffect(() => {
    const snap = prevScrollRef.current;
    if (snap.adjustLeft && containerRef.current && snap.width > 0) {
      const delta = containerRef.current.scrollWidth - snap.width;
      containerRef.current.scrollLeft += delta;
      prevScrollRef.current = { width: 0, adjustLeft: false };
    }
    setTimeout(() => { isExtending.current = false; }, 120);
  });

  const handleScroll = useCallback(() => {
    if (isExtending.current) return;
    const cont = containerRef.current;
    if (!cont) return;

    const nearLeft  = cont.scrollLeft < 200;
    const nearRight = cont.scrollLeft + cont.clientWidth > cont.scrollWidth - 200;

    if (nearLeft) {
      isExtending.current = true;
      prevScrollRef.current = { width: cont.scrollWidth, adjustLeft: true };
      setStartOffset(prev => {
        const next = prev - CHUNK;
        fetchDots(next, prev - 1);
        return next;
      });
    } else if (nearRight) {
      isExtending.current = true;
      setEndOffset(prev => {
        const next = prev + CHUNK;
        fetchDots(prev + 1, next);
        return next;
      });
    }
  }, [fetchDots]);

  const dates = buildDates(startOffset, endOffset);
  const selectedSet = new Set(selectedDates);

  return (
    <div
      ref={containerRef}
      onScroll={handleScroll}
      className="flex space-x-2 overflow-x-auto pb-2 scrollbar-thin snap-x"
      data-testid="date-strip"
    >
      {dates.map((d) => {
        const ymd       = toYMD(d);
        const isSelected  = selectedSet.has(ymd);
        const isToday   = ymd === today;
        const hasActivity = activityDates.has(ymd);

        const baseStyle = 'flex-shrink-0 snap-start w-14 flex flex-col items-center justify-center py-2 rounded-xl border transition-colors';
        let cls;
        if (isSelected) cls = c.selected;
        else if (isToday) cls = `bg-white ${c.today}`;
        else cls = 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50';

        return (
          <button
            key={ymd}
            ref={isToday ? todayRef : null}
            onClick={() => onSelectDate(ymd)}
            className={`${baseStyle} ${cls}`}
            data-testid={`date-strip-item-${ymd}`}
          >
            <span className="text-[10px] font-medium uppercase tracking-wide opacity-80">
              {DAYS[d.getDay()]}
            </span>
            <span className="text-lg font-bold leading-none my-0.5">{d.getDate()}</span>
            <span className="text-[10px] opacity-70">{MONTHS[d.getMonth()]}</span>
            <span
              className={`mt-1 h-1.5 w-1.5 rounded-full ${
                hasActivity ? (isSelected ? 'bg-white' : c.dot) : 'bg-transparent'
              }`}
            />
          </button>
        );
      })}
    </div>
  );
};

export default DateStrip;
